import React, { useEffect, useRef } from 'react';
import './styles.scss';
import { isStaff } from '../../../../helpers/global';
import useCountyCityOptions from '../../../../hooks/useCountyCityOptions';
import useCountyDefaultProcessors from '../../../../hooks/useCountyDefaultProcessors';
import { Col, Form, Row, Spinner } from 'react-bootstrap';
import RushCheckbox from '../RushCheckbox';
import AssignedProcessorsMultiSelect from '../../../AssignedProcessors';

const isSameSelection = (a = [], b = []) => a.length === b.length && a.every(id => b.includes(id));

export const LienSearch = ({
  defaultValues,
  role,
  city,
  formData,
  setFormData,
  formFieldError,
  setFormFieldError,
  initialFormFieldError
}) => {
  const { getCountyForCity } = useCountyCityOptions();
  const county = city ? getCountyForCity(city) : null;
  const { processors: countyDefaultProcessors, loading: loadingCountyDefaultProcessors } = useCountyDefaultProcessors(
    county,
    isStaff(role)
  );
  const preselectedCounty = useRef(null);

  useEffect(() => {
    if (Object.keys(defaultValues).length > 0) setFormData(defaultValues);
  }, [defaultValues]);

  // Accurate roles pick the processors here, so pre-select the county's default ones. When the county changes
  // the selection is replaced by the new county's. Other roles don't get the field; the backend assigns for them.
  useEffect(() => {
    if (!isStaff(role)) return;
    const selectedIds = formData.assignedProcessors || [];
    const isCountyChanged = !!preselectedCounty.current && preselectedCounty.current !== county;
    preselectedCounty.current = county;
    // A selection made before the first county (e.g. restored when coming back to this step) is kept
    if (!isCountyChanged && selectedIds.length) return;

    const defaultProcessorIds = countyDefaultProcessors.map(processor => processor._id);
    if (isSameSelection(selectedIds, defaultProcessorIds)) return;
    setFormData(prevData => ({ ...prevData, assignedProcessors: defaultProcessorIds }));
    if (defaultProcessorIds.length) setFormFieldError(initialFormFieldError);
  }, [countyDefaultProcessors]);

  const onFormDataChange = (field, e) => {
    if (e && e?.target) {
      const updatedFormData = { ...formData };
      updatedFormData[field] = e.target.value;
      setFormData(updatedFormData);
      setFormFieldError(initialFormFieldError);
    }
  };

  const handleAssignedProcessorsChange = e => {
    const assignedProcessorIds = e.map(a => a._id);
    setFormData(prevData => ({ ...prevData, assignedProcessors: assignedProcessorIds }));
    setFormFieldError(initialFormFieldError);
  };

  return (
    <>
      <p className="col-title my-2">Lien Search Details</p>
      <Row className="align-items-center">
        {isStaff(role) && (
          <>
            <Col md={3}>
              <Form.Group className="midFont">
                <Form.Label>
                  Assigned Processors<span className="text-danger">*</span>
                </Form.Label>
                <div className="position-relative">
                  <AssignedProcessorsMultiSelect
                    id="lienAssignedProcessors"
                    error={formFieldError.isAssignedProcessorsNotValid}
                    onChange={handleAssignedProcessorsChange}
                    selectedProcessorsID={formData.assignedProcessors}
                  />
                  {loadingCountyDefaultProcessors && (
                    <span
                      className="position-absolute d-flex text-muted"
                      style={{ right: 10, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }}
                    >
                      <Spinner size="sm" animation="border" />
                    </span>
                  )}
                </div>
                {formFieldError.isAssignedProcessorsNotValid && (
                  <p className="xsmallFont text-rush">Assigned Processors is required.</p>
                )}
              </Form.Group>
            </Col>
          </>
        )}

        <Col md={3} xs={12}>
          <RushCheckbox
            serviceName="Lien Search"
            serviceKey={'lienSearch'}
            checked={formData.rush}
            onCheckedChange={() => onFormDataChange('rush', { target: { value: !formData.rush } })}
          />
        </Col>
      </Row>
    </>
  );
};
